import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router'
import { useEffect } from 'react'
import {
  Breadcrumbs,
  CardsFilter,
  CalendarDropdown,
  FullPageWrapper,
  SpinnerLoader,
  Switch,
  ContainerHeader,
  variantActionsProps,
} from 'shared/ui'
import { IntegrationsRoutes, SettingsRoutes } from 'entities/ExportRouters'
import { type IWebhookHistoryStatusesKeys } from 'entities/Webhook'
import { useMenuOutboundLayoutContext } from 'widgets/Menu/ui/MenuOutbound'
import { useWebhookIntegrationContext } from 'pages/settings/pages/integrations/layout/WebhookLayout'
import { useSettingsContext } from 'pages/settings/context/settingsContext'
import { WebhookStatus } from 'pages/settings/pages/integrations/pages/Webhooks/ui/WebhookStatus/WebhookStatus'
import { HistoryTable } from 'pages/settings/pages/integrations/pages/Webhooks/ui/HistoryTable/HistoryTable'
import styles from './styles.module.scss'

export const HistoryPage = observer(() => {
  const { id } = useParams()
  const menuLayoutStore = useMenuOutboundLayoutContext()
  const { webhooksStore } = useWebhookIntegrationContext()
  const { pageLayoutStore: settingsPageLayoutStore } = useSettingsContext()

  const { headerMenuTooltipIconProps } = menuLayoutStore.pageLayoutStore
  const {
    webhookHistoryStore,
    updateStatus,
    onEdit,
    openDetailsModal,
    openDeleteModalWithCloseCallback,
  } = webhooksStore
  const {
    cardsFilters,
    calendarFilterStore,
    webhook,
    isLoading,
    updateWebhook,
    setActiveFilterKey,
    init,
  } = webhookHistoryStore

  const webhookId = Number(id)

  useEffect(() => {
    init(webhookId)
  }, [id])

  if (isLoading || !webhook) return <SpinnerLoader ariaLabel={'HistoryPage'} />

  return (
    <>
      <FullPageWrapper className={styles.historyContainer}>
        <Breadcrumbs
          className={styles.breadcrumbs}
          getLabel={() => 'All webhooks'}
          customPath={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}`}
        />
        <ContainerHeader
          paddingTop={0}
          paddingBottom={12}
          paddingLeft={4}
          paddingRight={4}
          title={webhook?.name}
          titleVariant={'heading-md'}
          titleIconProps={{
            icon: 'lineCollapseExpandLeft',
            fontSize: 20,
            color: 'var(--content-primary-tertiary)',
          }}
          actionIconTooltipProps={headerMenuTooltipIconProps}
          actionsProps={{
            ...variantActionsProps['headerMedium'],
            showItems: 3,
            isStrictShowItems: true,
            actions: [
              {
                key: 'status-switch',
                buttonComponent: (
                  <Switch
                    size='middle'
                    value={webhook.is_active}
                    className={styles.statusSwitch}
                    onChange={() =>
                      updateStatus(webhook, () =>
                        updateWebhook({ ...webhook, is_active: !webhook.is_active })
                      )
                    }
                  />
                ),
              },
              {
                key: 'edit',
                text: 'Edit',
                icon: 'edit',
                onAction: () => onEdit(webhook),
              },
              {
                key: 'details',
                text: 'Details',
                icon: 'eye',
                onAction: () => openDetailsModal(webhook),
              },
              {
                key: 'delete',
                text: 'Delete',
                icon: 'delete',
                onAction: () => openDeleteModalWithCloseCallback(webhook),
              },
            ],
          }}
          titleIconAction={settingsPageLayoutStore.handleToggleCollapse}
          ellipsis
        />
        <WebhookStatus className={styles.webhookStatus} isActive={webhook.is_active} />
        <CardsFilter
          items={cardsFilters}
          underline
          wrapperClassName={styles.filterWrapper}
          activeKey={webhookHistoryStore.activeFilterKey}
          onActiveFilter={(tab) => setActiveFilterKey(tab.key as IWebhookHistoryStatusesKeys)}
        />

        <div className={styles.dateRangeContainer}>
          <CalendarDropdown
            store={calendarFilterStore}
            onClose={() => {
              webhookHistoryStore.onCloseCalendar()
            }}
          />
        </div>

        <HistoryTable />
      </FullPageWrapper>
    </>
  )
})
